import type { ExplorerReport, ExplorerReportScope, ExplorerScopeType, ExplorerTemplateScope } from './types/explorer.types'

/**
 * Explorer client constants (MYD-361).
 *
 * IMPORTANT: `COLUMN_CAP` mirrors `COLUMN_CAP` in
 * `backend/explorer/constants.js`. The backend is the enforcer and returns a
 * 400 past the cap; this copy exists only so the picker can refuse the 41st
 * column with an explanation instead of letting the user build a report that
 * fails on its next run. Change one, change the other in the same PR — the same
 * arrangement `utilities/capabilities.ts` has with its backend twin.
 */
export const COLUMN_CAP = 40

/** Order the picker lists column groups in, ahead of any group it does not know. */
export const GROUP_ORDER = ['Location', 'Address', 'Size', 'Detail', 'People', 'Rollups']

/**
 * URL slug per grain key.
 *
 * The grain KEY is singular (`location`, `evaluationSite`) because it names one
 * row and is persisted on every saved report. The URL wants the plural, and
 * `/explorer/evaluationSite` would be an unpleasant thing to send someone. So
 * the two are mapped rather than conflated.
 *
 * This is not cosmetic: assuming they were the same is what made
 * `/explorer/locations` resolve to no grain, redirect to itself, and render a
 * blank page with no error in the console.
 */
export const GRAIN_SLUGS: Record<string, string> = {
	location: 'locations',
	project: 'projects',
	task: 'tasks',
	evaluationSite: 'sites',
}

/** The URL slug for a grain key, falling back to the key itself. */
export const slugForGrain = (key: string): string => GRAIN_SLUGS[key] ?? key

/** The grain key for a URL slug, or null when the slug names nothing. */
export const grainForSlug = (slug: string | undefined): string | null => {
	if (!slug) return null
	const entry = Object.entries(GRAIN_SLUGS).find(([, value]) => value === slug)
	return entry ? entry[0] : null
}

// =============================================================================
// Templates and asset scope (MYD-598)
// =============================================================================

/**
 * Which assets each grain can be narrowed to.
 *
 * Mirrors `SCOPE_TYPES_BY_GRAIN` in `backend/explorer/constants.js`, which is
 * the enforcer (a scope outside it is a 400). This copy only decides what the
 * UI offers. Change one, change the other.
 */
export const SCOPE_TYPES_BY_GRAIN: Record<string, ExplorerScopeType[]> = {
	task: ['project', 'location', 'portfolio'],
	project: ['location', 'portfolio'],
	location: ['portfolio'],
	evaluationSite: ['evaluation'],
}

/** The gallery tab an unscoped template of each grain sits under. Mirrors the backend. */
export const HOME_TEMPLATE_SCOPE_BY_GRAIN: Record<string, ExplorerTemplateScope> = {
	location: 'location',
	project: 'project',
	task: 'task',
	evaluationSite: 'evaluation',
}

/** The gallery tabs, in order, with their labels. */
export const TEMPLATE_TABS: Array<{ value: ExplorerTemplateScope; label: string }> = [
	{ value: 'location', label: 'Location' },
	{ value: 'project', label: 'Project' },
	{ value: 'task', label: 'Task' },
	{ value: 'portfolio', label: 'Portfolio' },
	{ value: 'evaluation', label: 'Evaluation' },
]

/** Plural row labels per grain, for "Tasks of: ..." without waiting on the grain list. */
export const GRAIN_LABELS: Record<string, string> = {
	location: 'Locations',
	project: 'Projects',
	task: 'Tasks',
	evaluationSite: 'Sites',
}

/**
 * The tabs a template of this grain can be filed under: each asset it can be
 * scoped to, then its own (unscoped). evaluationSite's own tab IS its scope
 * type, so it is listed once.
 */
export const templateTabsForGrain = (grain: string): ExplorerTemplateScope[] => {
	const tabs: ExplorerTemplateScope[] = [...(SCOPE_TYPES_BY_GRAIN[grain] ?? [])]
	const home = HOME_TEMPLATE_SCOPE_BY_GRAIN[grain]
	if (home && !tabs.includes(home)) tabs.push(home)
	return tabs
}

/**
 * Can this template be used on an asset of `type`, and if so does it bind?
 *
 * A template scoped to `type` binds to it. An unscoped one binds too when its
 * grain can be narrowed to `type`, so "Open tasks by assignee" started from a
 * project page shows that project's tasks. Anything else does not fit.
 */
export const templateFitsAsset = (template: ExplorerReport, type: ExplorerScopeType): boolean => {
	const bindsTo = template.definition?.scope?.type
	if (bindsTo) return bindsTo === type
	return (SCOPE_TYPES_BY_GRAIN[template.grain] ?? []).includes(type)
}

/**
 * Where using a template lands: Explorer on the template's grain, carrying the
 * template and asset in the URL so the draft survives a reload.
 */
export const templateDraftPath = (
	explorerRoot: string,
	template: Pick<ExplorerReport, 'id' | 'grain'>,
	scope?: ExplorerReportScope | null,
): string => {
	const params = new URLSearchParams({ template: template.id })
	if (scope?.id) {
		params.set('scopeType', scope.type)
		params.set('scopeId', scope.id)
	}
	return `${explorerRoot}/${slugForGrain(template.grain)}?${params.toString()}`
}

/** "task.taskName" -> "Task Name", for a column preview when the catalog is not loaded. */
export const humanizeColumnKey = (key: string): string => {
	const field = key.includes('.') ? key.slice(key.indexOf('.') + 1) : key
	const spaced = field.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[._-]+/g, ' ').trim()
	return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}
